import { Component, inject, input, output } from '@angular/core';
import { PronunciationService } from '../../../services/pronunciation.service';
import { TLingoTileResult } from '../../../utils/lingo.utils';
import { RidLingoMarquee, TLingoMarqueeMood } from '../rid-lingo-marquee/rid-lingo-marquee';

/** The klanken on the Lingo keyboard, per row and per group within a row. */
export const LINGO_KEYBOARD_ROWS = [
  [
    ['a', 'e', 'o', 'u', 'i'],
    ['aa', 'ee', 'oo', 'uu'],
  ],
  [['ei', 'ij', 'ie', 'au', 'ou', 'oe', 'eu', 'ui', 'uw', 'ch', 'ng', 'nk']],
  [['b', 'd', 'f', 'g', 'h', 'j', 'k', 'l', 'm', 'n', 'p', 'r', 's', 't', 'v', 'w', 'z']],
] as const;

export type TLingoKlank = (typeof LINGO_KEYBOARD_ROWS)[number][number][number];

// The key stands on an edge of --edge-color, as deep as it moves down when pressed
const KEY =
  'grid h-22 min-w-22 cursor-pointer place-items-center rounded-xl px-4 font-default text-[2.75rem] leading-none font-semibold select-none shadow-[0_8px_0_var(--edge-color)] transition-[background-color,translate,box-shadow] duration-100 enabled:active:translate-y-2 enabled:active:shadow-none';

const KLANK_KEY_STATES: Record<TLingoTileResult | 'NONE', string> = {
  NONE: 'bg-[#f7efdd] text-[#3a3226] [--edge-color:#c7b58f] enabled:hover:bg-[#fffaf0]',
  CORRECT: 'bg-[#5cc02f] text-white [--edge-color:#3d8a1b] enabled:hover:bg-[#6dce3f]',
  PRESENT: 'bg-[#f8c21a] text-white [--edge-color:#c08f00] enabled:hover:bg-[#ffd43f]',
  ABSENT: 'bg-[#9cc6ca] text-[#3f7a80] [--edge-color:#6fa3a8] enabled:hover:bg-[#acd2d5]',
};

const ERASE_KEY = 'bg-[#e5484d] text-white [--edge-color:#a8272b] enabled:hover:bg-[#ef5f63]';
const SUBMIT_KEY = 'bg-[#3b82f6] text-white [--edge-color:#1d4fb8] enabled:hover:bg-[#5896f8]';
const DISABLED_ACTION_KEY = 'cursor-default bg-[#aab4b8] text-[#e3e8ea] [--edge-color:#7d8a8f]';

@Component({
  selector: 'rid-lingo-keyboard',
  imports: [RidLingoMarquee],
  templateUrl: './rid-lingo-keyboard.html',
  host: {
    class: 'block',
  },
})
export class RidLingoKeyboard {
  private readonly $pronunciationService = inject(PronunciationService);

  readonly rows = LINGO_KEYBOARD_ROWS;

  disabled = input<boolean>(false);
  /** The best result each klank got in the guesses so far, to colour the keys like the board. */
  keyStates = input<ReadonlyMap<string, TLingoTileResult>>(new Map());
  canErase = input<boolean>(false);
  canSubmit = input<boolean>(false);
  /** How the bulbs around the keyboard light up. */
  marqueeMood = input<TLingoMarqueeMood>('IDLE');

  typed = output<TLingoKlank>();
  erased = output<void>();
  submitted = output<void>();

  klankKeyClass(klank: TLingoKlank): string {
    return `${KEY} ${KLANK_KEY_STATES[this.keyStates().get(klank) ?? 'NONE']} disabled:cursor-default disabled:opacity-60`;
  }

  eraseKeyClass(): string {
    return `${KEY} ${this.canErase() ? ERASE_KEY : DISABLED_ACTION_KEY}`;
  }

  submitKeyClass(): string {
    return `${KEY} ${this.canSubmit() ? SUBMIT_KEY : DISABLED_ACTION_KEY}`;
  }

  type(klank: TLingoKlank) {
    if (this.disabled()) return;

    void this.$pronunciationService.tryPronounce(klank);
    this.typed.emit(klank);
  }
}
